/*
Government Purpose Rights (“GPR”)
Contract No.  W911NF-14-D-0005
Contractor Name:   University of Southern California
Contractor Address:  3720 S. Flower Street, 3rd Floor, Los Angeles, CA 90089-0001
Expiration Date:  Restrictions do not expire, GPR is perpetual
Restrictions Notice/Marking: The Government's rights to use, modify, reproduce, release, perform, display, or disclose this software are restricted by paragraph (b)(2) of the Rights in Noncommercial Computer Software and Noncommercial Computer Software Documentation clause contained in the above identified contract.  No restrictions apply after the expiration date shown above. Any reproduction of the software or portions thereof marked with this legend must also reproduce the markings. (see: DFARS 252.227-7014(f)(2)) 
No Commercial Use: This software shall be used for government purposes only and shall not, without the express written permission of the party whose name appears in the restrictive legend, be used, modified, reproduced, released, performed, or displayed for any commercial purpose or disclosed to a person other than subcontractors, suppliers, or prospective subcontractors or suppliers, who require the software to submit offers for, or perform, government contracts.  Prior to disclosing the software, the Contractor shall require the persons to whom disclosure will be made to complete and sign the non-disclosure agreement at 227.7103-7.  (see DFARS 252.227-7025(b)(2))
*/
import React from "react";
import {
  CircularProgressbar,
  CircularProgressbarWithChildren,
} from "react-circular-progressbar";
import { Goal, Topic } from "@ictlearningsciences/pal3-core/types";
import { makeStyles } from "tss-react/mui";
import _ from "lodash";
import {
  Grid,
  LinearProgress,
  linearProgressClasses,
  styled,
  Typography,
} from "@mui/material";

import { useWithMastery } from "../hooks/useWithMastery";
import { useAppSelector } from "../store/hooks";
import { useWithWindow } from "../hooks/useWithWindow";

const useStyles = makeStyles()(() => ({
  goalMasteryIcon: {
    display: "flex",
    padding: 5,
    borderRadius: 10,
    fontSize: 12,
    fontWeight: "bold",
    alignItems: "center",
    height: 50,
    justifyContent: "center",
  },
}));

const VerticalLinearProgress = styled(LinearProgress)(() => ({
  width: 3,
  height: "100%",
  [`& .${linearProgressClasses.bar}`]: {
    backgroundColor: "#D9D9D9",
  },
  [`&.${linearProgressClasses.colorSecondary}`]: {
    backgroundColor: "#5CB1D4",
  },
  marginRight: "none",
}));

function Separator(props: {
  turns: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  style: any;
}) {
  return (
    <div
      style={{
        position: "absolute",
        height: "100%",
        transform: `rotate(${props.turns}turn)`,
      }}
    >
      <div style={props.style} />
    </div>
  );
}

function RadialSeparators(props: {
  count: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  style: any;
}): // eslint-disable-next-line @typescript-eslint/no-explicit-any
any {
  const turns = 1 / props.count;
  return _.range(props.count).map((index) => (
    <Separator key={`${index}`} turns={index * turns} style={props.style} />
  ));
}

export function GoalMasteryIcon(props: { goal: Goal }): JSX.Element {
  const { classes } = useStyles();
  const [mastery, setMastery] = React.useState<number>(0);
  const { getMasteryForGoal } = useWithMastery();
  const { goalMastery } = useAppSelector((state) => state.userData);
  const [progressColor, setProgressColor] = React.useState<string>();

  React.useEffect(() => {
    if (props.goal) {
      const d = getMasteryForGoal(props.goal);
      const temp = Math.round(d.mastery * 100);
      setMastery(Number.isNaN(temp) ? 0 : temp);
      setProgressColor("#83C8E4");
    }
  }, [props.goal, goalMastery]);

  return (
    <div data-cy="goal-mastery-icon" style={{ width: 45 }}>
      <Grid container className={classes.goalMasteryIcon}>
        <Grid
          container
          width={40}
          justifyContent={"center"}
          alignContent={"center"}
        >
          <Grid item>
            <div style={{ width: 3, height: 25 }}>
              <VerticalLinearProgress
                aria-label={`Goal ${props.goal.name} mastery bar: ${
                  mastery * 100
                }%`}
                variant="determinate"
                color="secondary"
                value={mastery}
                sx={{
                  [`& .${linearProgressClasses.bar}`]: {
                    transform: `translateY(${-mastery}%)!important`,
                  },
                }}
              />
            </div>
          </Grid>
          <Grid item>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: 25,
                height: 25,
                backgroundColor: progressColor,
                filter:
                  mastery == 0
                    ? `brightness(${mastery + 75}%)`
                    : `brightness(${Math.min(75 + mastery * 0.25, 100)}%)`,
              }}
            >
              <Typography style={{ color: "white" }}>M</Typography>
            </div>
          </Grid>
          <Grid item>
            <div style={{ width: 3, height: 25 }}>
              <VerticalLinearProgress
                variant="determinate"
                color="secondary"
                value={mastery}
                sx={{
                  [`& .${linearProgressClasses.bar}`]: {
                    transform: `translateY(${-mastery}%)!important`,
                  },
                }}
              />
            </div>
          </Grid>
        </Grid>
        <Grid container width={20} justifyContent={"center"}>
          {mastery}%
        </Grid>
      </Grid>
    </div>
  );
}

export function TopicMasteryIcon(props: { topic: Topic }): JSX.Element {
  const [mastery, setMastery] = React.useState<number>(0);
  const { getMasteryForTopic } = useWithMastery();
  const { topicMastery } = useAppSelector((state) => state.userData);
  const { width: screenWidth } = useWithWindow();
  const SMALL_SIZE = 50;
  const BIG_SIZE = 60;
  const targetSize = screenWidth > 1300 ? BIG_SIZE : SMALL_SIZE;

  React.useEffect(() => {
    if (props.topic) {
      const d = getMasteryForTopic(props.topic);
      setMastery(Math.round(d.mastery * 100));
    }
  }, [props.topic, topicMastery[props?.topic.id]]);

  return (
    <div
      style={{
        height: targetSize,
        width: targetSize,
        minHeight: targetSize,
        minWidth: targetSize,
        alignSelf: "center",
      }}
    >
      <CircularProgressbarWithChildren
        value={mastery}
        text={`${mastery}%`}
        background
        backgroundPadding={5}
        strokeWidth={10}
        styles={{
          path: {
            stroke: "white",
            strokeLinecap: "butt",
            transitionDuration: "1s",
          },
          trail: {
            stroke: "#565656",
          },
          text: {
            fill: "white",
            fontSize: 24,
            fontWeight: "bold",
          },
          background: {
            fill: "#7dbbd4",
          },
        }}
      >
        <RadialSeparators
          count={10}
          style={{
            background: "#7dbbd4",
            width: 2,
            height: 10,
          }}
        />
      </CircularProgressbarWithChildren>
    </div>
  );
}

export function TopicMasteryWithDifference(props: {
  old: number;
  new: number;
}): JSX.Element {
  const [oldMastery, setOldMastery] = React.useState<number>(0);
  const [newMastery, setNewMastery] = React.useState<number>(0);
  const [topicMasteryDiff, setTopicMasteryDiff] = React.useState<string>("+0%");

  React.useEffect(() => {
    setOldMastery(Math.round(props.old * 100));
    setNewMastery(Math.round(props.new * 100));
    setTopicMasteryDiff(getMasteryDiff(props.new, props.old));
  }, [props.old, props.new]);

  function getMasteryDiff(n: number, o: number): string {
    const diff = Math.round((n - o) * 100);
    return `${diff >= 0 ? "+" : "-"}${Math.abs(diff)}%`;
  }

  return (
    <div
      data-cy="topic-mastery-icon"
      style={{
        display: "flex",
        flexDirection: "column",
        height: 40,
        width: 40,
        minHeight: 40,
        minWidth: 40,
        alignItems: "center",
      }}
    >
      <CircularProgressbarWithChildren
        value={newMastery > oldMastery ? newMastery : oldMastery}
        text={`${newMastery}%`}
        background
        backgroundPadding={5}
        strokeWidth={10}
        styles={{
          path: {
            stroke: newMastery > oldMastery ? "#b3ffb3" : "#ff0000",
            strokeLinecap: "butt",
            transitionDuration: "1s",
          },
          trail: {
            stroke: "#565656",
          },
          text: {
            fill: "white",
            fontSize: 24,
            fontWeight: "bold",
          },
          background: {
            fill: "#7dbbd4",
          },
        }}
      >
        <CircularProgressbar
          value={newMastery > oldMastery ? oldMastery : newMastery}
          background
          backgroundPadding={5}
          strokeWidth={10}
          styles={{
            path: {
              stroke: "white",
              strokeLinecap: "butt",
              transitionDuration: "1s",
            },
            trail: {
              stroke: "transparent",
            },
            background: {
              fill: "transparent",
            },
          }}
        />
        <RadialSeparators
          count={10}
          style={{
            background: "#7dbbd4",
            width: 2,
            height: 10,
          }}
        />
      </CircularProgressbarWithChildren>
      <Typography
        style={{
          fontSize: 10,
          color: "gray",
        }}
      >
        {topicMasteryDiff}
      </Typography>
    </div>
  );
}
